import { AppMenu } from '@/catalog-apps/components'
import { MenuLoading } from '@/catalog-apps/components/AppMenu/AppMenu'
import { FiltersLayoutProvider } from '@/catalog-apps/components/AppPage2'
import { AppPageLoading } from '@/catalog-apps/components/AppPage2/AppPage2'
import { useAppPageState } from '@/catalog-apps/components/AppPage2/useAppPageState'
import { AppSupportMenuAiChatSheetHost } from '@/catalog-apps/components/AppSupportMenuAiChat/AppSupportMenuAiChat'
import { AppSupportMenu, AppSupportMenuLoading, SupportPanelView } from '@/catalog-apps/components/AppSupportMenu'
import { MAIN_CONTENT_HEIGHT, SIDEBAR_WIDTH } from '@/catalog-apps/components/config'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { SupportPanelComponentRenderer } from '@/catalog-apps/components/SupportPanel'
import { SupportPanelLoading } from '@/catalog-apps/components/SupportPanel/SupportPanelLoading'
import { SupportPanelV2 } from '@/catalog-apps/components/SupportPanel/SupportPanelV2'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { ConnectionHubProvider } from '@/catalog-apps/hooks/useConnectionHub'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Sidebar, SidebarContent, SidebarProvider } from '@/components/shadcn/sidebar'
import { SidebarToggleButton } from '@/components/ui/SidebarToggleButton'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { useSharedSidebarState } from '@/hooks'
import { getCssVars } from '@/theme/appTheme'
import { OrganisationIcon } from '@/const'
import { LayoutGroup, motion } from 'motion/react'
import { Suspense, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Outlet } from 'react-router'
import { DEFAULT_HEADER_STATE, useSetHeaderContext } from '../MainLayout/HeaderContext'
import { TermsAcceptanceDialog } from './TermsAcceptanceDialog'
import { useGetAppMenu } from '@/catalog-apps/components/AppMenu/useGetAppMenu'

type MessagesWorkspacesWorkspaceCardFixed = Omit<
  RestSchemas['Messages.Workspaces.WorkspaceCard'],
  'coBrandingImageKey'
> & {
  cobranding_imagekey?: string
}

export type SupportPanelState = {
  supportPanelView?: SupportPanelView
  setSupportPanelView: React.Dispatch<React.SetStateAction<SupportPanelView | undefined>>
}

export function CatalogLayout() {
  const [sidebarOpen, setSidebarOpen] = useSharedSidebarState(true)
  const [supportPanelView, setSupportPanelView] = useState<SupportPanelView>()
  const { appKey, projectId } = useAppContext()
  const { getWorkspacesApiGetWorkspace } = useRestClient()
  const {
    data: { items: menuItems },
  } = useGetAppMenu()
  const showSidebar = menuItems.length > 1 || (menuItems.length === 1 && menuItems[0].items.length > 0)
  const {
    data: { theme },
  } = getWorkspacesApiGetWorkspace.useSuspenseQuery(
    {
      path: { appKey: appKey!, projectId: projectId! },
    },
    {
      staleTime: 1000 * 60 * 60, // 1 hour
    }
  )

  const themeVars = useMemo(() => {
    let parsedTheme = {}
    if (theme) {
      try {
        parsedTheme = JSON.parse(theme)
      } catch (e) {
        console.warn('Failed to parse workspace theme JSON', e)
      }
    }
    return getCssVars(parsedTheme)
  }, [theme])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }
    const root = document.documentElement
    const entries = Object.entries(themeVars)

    entries.forEach(([key, value]) => {
      if (value != null) {
        root.style.setProperty(key, String(value))
      }
    })

    return () => {
      entries.forEach(([key]) => {
        root.style.removeProperty(key)
      })
    }
  }, [themeVars])

  return (
    <ConnectionHubProvider>
      <AppSupportMenuAiChatSheetHost />
      <Suspense fallback={<div className="flex h-full w-full items-center justify-center">Loading...</div>}>
        <LayoutGroup>
          <SidebarProvider
            data-app-theme={appKey}
            open={sidebarOpen}
            onOpenChange={setSidebarOpen}
            style={
              {
                '--sidebar-width': `${SIDEBAR_WIDTH}px`,
                '--sidebar-width-icon': '55px',
                height: MAIN_CONTENT_HEIGHT,
                gridTemplateColumns: showSidebar ? 'auto max-content 1fr' : 'max-content 1fr',
              } as CSSProperties
            }
            className="grid w-[100vw]"
          >
            {showSidebar && (
              <Sidebar style={{ height: MAIN_CONTENT_HEIGHT }} className="relative border-r-0 bg-offwhite">
                <div className="relative flex h-full flex-col justify-between gap-y-5">
                  <SidebarToggleButton onToggle={() => setSidebarOpen((curr) => !curr)} open={sidebarOpen} />
                  <SidebarContent className="min-h-0">
                    <ScrollArea
                      className="flex-1 border-b pb-5 pl-2 pt-14"
                      viewportClassName={sidebarOpen ? '[&>div]:!block [&>div]:!w-full' : ''}
                      scrollBarClassName="w-2"
                    >
                      <Suspense fallback={<MenuLoading />}>
                        <QueryErrorBoundary errorMessage={<span>Failed to fetch app menu</span>}>
                          <AppMenu sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                        </QueryErrorBoundary>
                      </Suspense>
                    </ScrollArea>
                  </SidebarContent>
                  <div className="h-min w-full pb-16 pl-3">
                    <Suspense fallback={<AppSupportMenuLoading />}>
                      <QueryErrorBoundary errorMessage={<span>Failed to fetch app support menu</span>}>
                        <AppSupportMenu setSupportPanelView={setSupportPanelView} sidebarOpen={sidebarOpen} />
                      </QueryErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </Sidebar>
            )}
            <motion.div layout style={{ height: MAIN_CONTENT_HEIGHT }} className={supportPanelView ? 'pl-5' : ''}>
              <SupportPanelV2 supportPanelView={supportPanelView} setSupportPanelView={setSupportPanelView}>
                <QueryErrorBoundary errorMessage={<span>Failed to fetch items</span>}>
                  <Suspense fallback={<SupportPanelLoading />}>
                    {/* <SupportPanelLoading /> */}
                    <SupportPanelComponentRenderer
                      supportPanelView={supportPanelView}
                      setSupportPanelView={setSupportPanelView}
                    />
                  </Suspense>
                </QueryErrorBoundary>
              </SupportPanelV2>
            </motion.div>
            <QueryErrorBoundary errorMessage={<span>Failed to fetch workspace details / load page</span>}>
              <Suspense fallback={<AppPageLoading />}>
                <Outlet />
              </Suspense>
            </QueryErrorBoundary>
          </SidebarProvider>
        </LayoutGroup>
      </Suspense>
    </ConnectionHubProvider>
  )
}

export function AppPageLayout() {
  const { setIsConfiguringLayout } = useAppPageState()
  const { appKey, appId, projectId, pageId, workspaceId } = useAppContext()
  const { getWorkspacesApiGetWorkspace } = useRestClient()
  const { data: workspace } = getWorkspacesApiGetWorkspace.useSuspenseQuery(
    {
      path: { appKey: appKey!, projectId: projectId! },
    },
    {
      staleTime: 1000 * 60 * 60, // 1 hour
    }
  )

  useEffect(() => {
    setIsConfiguringLayout(false)
  }, [setIsConfiguringLayout])

  const { application, project, organisation, privacy_statement_url, cobranding_imagekey } =
    workspace as MessagesWorkspacesWorkspaceCardFixed
  const workspacePath = `/workspaces/${workspaceId}?returnPage=${pageId}`

  useSetHeaderContext({
    ...DEFAULT_HEADER_STATE,
    coBrandingImageKey: cobranding_imagekey,
    title1: application,
    title2: project,
    titleUrl: workspacePath,
    titleNavigation: {
      current: 'application',
      target: workspacePath,
    },
    rightTitle: organisation ? { text: organisation, icon: OrganisationIcon } : undefined,
    privacyUrl: privacy_statement_url ?? undefined,
    usePrivacyPolicyDialog: true,
    privacyPolicyApplicationId: appId,
  })

  return (
    <motion.div layout style={{ width: '100%', height: MAIN_CONTENT_HEIGHT }}>
      <TermsAcceptanceDialog appId={appId} appKey={appKey} projectId={projectId} workspaceId={workspaceId} />
      <FiltersLayoutProvider>
        <div className="flex h-full w-full">
          <Outlet />
        </div>
      </FiltersLayoutProvider>
    </motion.div>
  )
}
